import {
  ArrowLeftRight,
  BadgePercent,
  BookOpenText,
  BriefcaseBusiness,
  ChartColumnBig,
  House,
  Inbox,
  LayoutGrid,
  MessagesSquare,
  ReceiptText,
  Settings,
  Sparkles,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import type { Feature } from '@outss/ledger-core';

/**
 * Screens that exist in the information architecture but are not built yet. This is the one
 * source of truth for "Coming soon": menus, tabs and links to these paths render disabled, and
 * a direct URL shows a "This feature isn't available yet" page. Remove a path when its screen
 * ships. (Which roadmap phase delivers each one is in docs/11, not in the UI.)
 */
export const UNAVAILABLE: Record<string, string> = {
  '/banking/receipts': 'Receipts',
  '/banking/deposits/new': 'Bank deposit',
  '/sales/refund-receipts/new': 'Refund receipt',
  '/practice/team': 'Team',
  '/settings/templates': 'Invoice templates',
};

/** Detail pages (".../{id}") of documents whose screens are not built yet. */
export const UNAVAILABLE_PREFIXES: string[] = [];

/** False for a screen that is not built yet (see UNAVAILABLE). */
export const isAvailable = (to: string): boolean => {
  const path = to.split('?')[0]!.replace(/\/+$/, '');
  return !(path in UNAVAILABLE) && !UNAVAILABLE_PREFIXES.some((p) => path.startsWith(p));
};

/** Tooltip on disabled entries. */
export const COMING_SOON = 'Coming soon';

export interface NavItem {
  label: string;
  to: string;
  /** Opens a quick-create drawer over the current page instead of navigating (QBO + New). */
  drawer?: 'customer' | 'vendor' | 'item';
  /** Shown only while this per-company feature is switched on (Company settings). */
  flag?: 'einvoicing' | 'projects';
  /** Shown only for clients on this tax pack (India GST or UAE VAT). */
  pack?: 'uae' | 'in';
  /** Shown only while one of these features is on for the client (ADR-0017). */
  features?: readonly Feature[];
}

export interface NavGroup {
  label: string;
  icon: LucideIcon;
  to?: string;
  items?: NavItem[];
  /** Outss staff only (Accountant view). */
  staffOnly?: boolean;
  /** Shown only while one of these features is on for the client (ADR-0017). */
  features?: readonly Feature[];
  /** A count shown beside a single-link entry (unread messages). */
  badge?: 'messages';
}

/** The client portal's documents: uploads and approvals (both views). */
const DOCUMENTS: NavGroup = {
  label: 'Documents',
  icon: Inbox,
  items: [
    { label: 'Inbox', to: '/documents/inbox' },
    { label: 'Approvals', to: '/documents/approvals' },
  ],
};

/** Chat between the client's people and Outss (document 06 C5; both views). */
const MESSAGES: NavGroup = {
  label: 'Messages',
  icon: MessagesSquare,
  to: '/messages',
  badge: 'messages',
};

/** Left navigation, Accountant view (document 05, "Left navigation"). Order and grouping follow QBO. */
export const NAVIGATION: NavGroup[] = [
  { label: 'Dashboard', icon: LayoutGrid, to: '/dashboard' },
  {
    label: 'Transactions',
    icon: ArrowLeftRight,
    items: [
      { label: 'Bank transactions', to: '/banking', features: ['banking'] },
      { label: 'Receipts', to: '/banking/receipts', features: ['banking'] },
      { label: 'Reconcile', to: '/banking/reconcile', features: ['banking'] },
      { label: 'Rules', to: '/banking/rules', features: ['banking'] },
      { label: 'Chart of accounts', to: '/accounting/chart-of-accounts' },
    ],
  },
  {
    label: 'Sales',
    icon: ReceiptText,
    features: ['invoices'],
    items: [
      { label: 'Overview', to: '/sales', features: ['invoices'] },
      { label: 'All sales', to: '/sales/all', features: ['invoices'] },
      { label: 'Invoices', to: '/sales/invoices', features: ['invoices'] },
      { label: 'Quotes', to: '/sales/quotes', features: ['sales'] },
      { label: 'Sales orders', to: '/sales/sales-orders', features: ['sales'] },
      {
        label: 'Delivery challans',
        to: '/sales/delivery-challans',
        pack: 'in',
        features: ['sales'],
      },
      { label: 'Customers', to: '/sales/customers', features: ['invoices'] },
      { label: 'Products and services', to: '/sales/products-and-services' },
      { label: 'Projects', to: '/projects', flag: 'projects' },
    ],
  },
  {
    label: 'Expenses',
    icon: Wallet,
    features: ['purchases', 'expenses'],
    items: [
      { label: 'Expenses', to: '/expenses', features: ['expenses'] },
      { label: 'Bills', to: '/expenses/bills', features: ['purchases'] },
      { label: 'Purchase orders', to: '/expenses/purchase-orders', features: ['purchases'] },
      { label: 'Vendors', to: '/expenses/vendors', features: ['purchases', 'expenses'] },
    ],
  },
  DOCUMENTS,
  MESSAGES,
  // bills and receipts read and categorised for review (document 06 AI1); staff only
  { label: 'AI Accountant', icon: Sparkles, to: '/ai-accountant', staffOnly: true },
  { label: 'Reports', icon: ChartColumnBig, to: '/reports', features: ['reports'] },
  {
    label: 'Taxes',
    icon: BadgePercent,
    features: ['taxes'],
    items: [
      { label: 'GST', to: '/taxes/gst', pack: 'in', features: ['taxes'] },
      { label: 'VAT', to: '/taxes/vat', pack: 'uae', features: ['taxes'] },
      { label: 'Corporate tax', to: '/taxes/corporate-tax', pack: 'uae', features: ['taxes'] },
      {
        label: 'E-invoicing',
        to: '/taxes/e-invoicing',
        flag: 'einvoicing',
        pack: 'uae',
        features: ['taxes'],
      },
    ],
  },
  {
    label: 'Accounting',
    icon: BookOpenText,
    items: [
      { label: 'Chart of accounts', to: '/accounting/chart-of-accounts' },
      { label: 'Journal entries', to: '/accounting/journal-entries' },
      { label: 'Reconcile', to: '/banking/reconcile', features: ['banking'] },
      { label: 'Periods and close', to: '/accounting/periods' },
      { label: 'Audit log', to: '/accounting/audit-log' },
      { label: 'Recurring transactions', to: '/accounting/recurring' },
      { label: 'Fixed assets', to: '/accounting/fixed-assets' },
      { label: 'Budgets', to: '/accounting/budgets' },
    ],
  },
  {
    label: 'My practice',
    icon: BriefcaseBusiness,
    staffOnly: true,
    items: [
      { label: 'Clients', to: '/practice/clients' },
      { label: 'Team', to: '/practice/team' },
    ],
  },
  { label: 'Settings', icon: Settings, to: '/settings/company' },
];

/**
 * Left navigation, Business view (document 05): client users, read-mostly, with what needs the
 * client (documents, approvals) up front.
 */
export const BUSINESS_NAVIGATION: NavGroup[] = [
  { label: 'Home', icon: House, to: '/dashboard' },
  DOCUMENTS,
  MESSAGES,
  {
    label: 'Sales',
    icon: ReceiptText,
    features: ['invoices'],
    items: [
      { label: 'Invoices', to: '/sales/invoices', features: ['invoices'] },
      { label: 'Customers', to: '/sales/customers', features: ['invoices'] },
    ],
  },
  {
    label: 'Expenses',
    icon: Wallet,
    features: ['purchases', 'expenses'],
    items: [
      { label: 'Bills', to: '/expenses/bills', features: ['purchases'] },
      { label: 'Expenses', to: '/expenses', features: ['expenses'] },
      { label: 'Vendors', to: '/expenses/vendors', features: ['purchases', 'expenses'] },
    ],
  },
  { label: 'Reports', icon: ChartColumnBig, to: '/reports', features: ['reports'] },
  {
    label: 'Taxes',
    icon: BadgePercent,
    features: ['taxes'],
    items: [
      { label: 'GST', to: '/taxes/gst', pack: 'in', features: ['taxes'] },
      { label: 'VAT', to: '/taxes/vat', pack: 'uae', features: ['taxes'] },
    ],
  },
  { label: 'Settings', icon: Settings, to: '/settings/notifications' },
];

/** "+ New" menu, four columns (document 05). */
export const NEW_MENU: Array<{ heading: string; items: NavItem[] }> = [
  {
    heading: 'Customers',
    items: [
      { label: 'Invoice', to: '/sales/invoices/new', features: ['invoices'] },
      { label: 'Receive payment', to: '/sales/receive-payment/new', features: ['invoices'] },
      { label: 'Quote', to: '/sales/quotes/new', features: ['sales'] },
      { label: 'Sales order', to: '/sales/sales-orders/new', features: ['sales'] },
      {
        label: 'Delivery challan',
        to: '/sales/delivery-challans/new',
        pack: 'in',
        features: ['sales'],
      },
      { label: 'Credit note', to: '/sales/credit-notes/new', features: ['invoices'] },
      { label: 'Sales receipt', to: '/sales/sales-receipts/new', features: ['sales'] },
      { label: 'Refund receipt', to: '/sales/refund-receipts/new', features: ['sales'] },
      { label: 'Customer', to: '/sales/customers', drawer: 'customer', features: ['invoices'] },
    ],
  },
  {
    heading: 'Vendors',
    items: [
      { label: 'Expense', to: '/expenses/expenses/new', features: ['expenses'] },
      { label: 'Bill', to: '/expenses/bills/new', features: ['purchases'] },
      { label: 'Pay bills', to: '/expenses/pay-bills', features: ['purchases'] },
      { label: 'Purchase order', to: '/expenses/purchase-orders/new', features: ['purchases'] },
      { label: 'Debit note', to: '/expenses/debit-notes/new', features: ['purchases'] },
      {
        label: 'Vendor',
        to: '/expenses/vendors',
        drawer: 'vendor',
        features: ['purchases', 'expenses'],
      },
    ],
  },
  {
    heading: 'Banking',
    items: [
      { label: 'Bank deposit', to: '/banking/deposits/new', features: ['banking'] },
      { label: 'Transfer', to: '/banking/transfers/new', features: ['banking'] },
      { label: 'Import statement', to: '/banking/import', features: ['banking'] },
    ],
  },
  {
    heading: 'Other',
    items: [
      { label: 'Journal entry', to: '/accounting/journal-entries/new' },
      { label: 'Recurring transaction', to: '/accounting/recurring/new' },
      { label: 'Statement', to: '/sales/statements/new' },
      { label: 'Inventory qty adjustment', to: '/inventory/adjust', features: ['inventory'] },
      { label: 'Product/service', to: '/sales/products-and-services', drawer: 'item' },
    ],
  },
];

/** Settings gear menu, four columns (document 05). */
export const GEAR_MENU: Array<{ heading: string; items: NavItem[] }> = [
  {
    heading: 'Your company',
    items: [
      { label: 'Company settings', to: '/settings/company' },
      { label: 'Manage users', to: '/settings/users' },
      { label: 'Invoice templates', to: '/settings/templates' },
      { label: 'Chart of accounts', to: '/accounting/chart-of-accounts' },
    ],
  },
  {
    heading: 'Lists',
    items: [
      { label: 'Products and services', to: '/sales/products-and-services' },
      { label: 'Recurring transactions', to: '/accounting/recurring' },
      { label: 'Attachments', to: '/settings/attachments' },
      { label: 'Tax rates', to: '/taxes/vat/rates' },
      { label: 'Currencies', to: '/settings/currencies' },
      { label: 'Classes', to: '/settings/classes' },
      { label: 'Locations', to: '/settings/locations' },
      { label: 'Tags', to: '/settings/tags' },
    ],
  },
  {
    heading: 'Tools',
    items: [
      { label: 'Import data', to: '/settings/import' },
      { label: 'Export data', to: '/settings/export' },
      { label: 'Sent emails', to: '/settings/sent-emails' },
      { label: 'Reconcile', to: '/banking/reconcile', features: ['banking'] },
      { label: 'Periods and close', to: '/accounting/periods' },
      { label: 'Audit log', to: '/accounting/audit-log' },
      { label: 'Move from QuickBooks', to: '/settings/migration' },
      { label: 'Budgets', to: '/accounting/budgets' },
    ],
  },
  {
    heading: 'Profile',
    items: [
      { label: 'Switch client', to: '/practice/clients' },
      { label: 'Profile and security', to: '/settings/profile' },
      { label: 'Notifications', to: '/settings/notifications' },
      { label: 'Keyboard shortcuts', to: '/help/shortcuts' },
    ],
  },
];

/**
 * Settings gear menu, Business view: only what client users may use (document 06 "Who: O, V").
 * Company setup, lists, imports and the period close stay with the accountant.
 */
export const BUSINESS_GEAR_MENU: Array<{ heading: string; items: NavItem[] }> = [
  {
    heading: 'Your company',
    items: [
      { label: 'Attachments', to: '/settings/attachments' },
      { label: 'Sent emails', to: '/settings/sent-emails' },
      { label: 'Export data', to: '/settings/export' },
    ],
  },
  {
    heading: 'Profile',
    items: [
      { label: 'Profile and security', to: '/settings/profile' },
      { label: 'Notifications', to: '/settings/notifications' },
      { label: 'Keyboard shortcuts', to: '/help/shortcuts' },
    ],
  },
];

/** Accountant tools (briefcase in the top bar, Outss staff only; document 05 "Top bar"). */
export const ACCOUNTANT_TOOLS: NavItem[] = [
  { label: 'AI Accountant', to: '/ai-accountant' },
  { label: 'Journal entry', to: '/accounting/journal-entries/new' },
  { label: 'Reclassify transactions', to: '/accounting/reclassify' },
  { label: 'Write off invoices', to: '/accounting/write-off-invoices' },
  { label: 'Reconcile', to: '/banking/reconcile', features: ['banking'] },
  { label: 'Periods and close', to: '/accounting/periods' },
  { label: 'Audit log', to: '/accounting/audit-log' },
  { label: 'Voided and reversed transactions', to: '/reports/voided-reversed' },
  { label: 'Trial balance', to: '/reports/trial-balance' },
  { label: 'Management reports', to: '/reports?tab=management' },
  { label: 'Budgets', to: '/accounting/budgets' },
  { label: 'Fixed assets', to: '/accounting/fixed-assets' },
];
